import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { providerAPI, adminAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Calendar, Clock, Users, DollarSign, TrendingUp, MapPin,
  Phone, Mail, Building2, BarChart3, LogOut, ChevronRight,
  CheckCircle, AlertCircle, Loader2
} from 'lucide-react';

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export default function ProviderDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [operatorData, setOperatorData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tours, setTours] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'provider') {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load dashboard data from backend API
      const dashboardRes = await providerAPI.getDashboard();
      const data = dashboardRes.data;
      
      if (data.operator) {
        setOperatorData(data.operator);
        setTours(data.tours || []);
        setBookings(data.bookings || []);
        setStats(data.stats);
      } else {
        // Operator profile not found
        setOperatorData(null);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!operatorData) {
    return (
      <div className="min-h-screen bg-muted/30">
        <div className="container mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 mx-auto mb-4 text-amber-500" />
          <h1 className="font-heading text-2xl font-bold mb-4">Provider Account Not Found</h1>
          <p className="text-muted-foreground mb-8">
            Your account hasn't been linked to an operator profile yet. 
            Please contact the Tour Finder team to complete your setup.
          </p>
          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Calculate dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Filter bookings
  const todayBookings = bookings.filter(b => {
    const bookingDate = new Date(b.date || b.tour_date);
    bookingDate.setHours(0, 0, 0, 0);
    return bookingDate.getTime() === today.getTime() && b.status !== 'cancelled';
  });

  const tomorrowBookings = bookings.filter(b => {
    const bookingDate = new Date(b.date || b.tour_date);
    bookingDate.setHours(0, 0, 0, 0);
    return bookingDate.getTime() === tomorrow.getTime() && b.status !== 'cancelled';
  });

  const futureBookings = bookings.filter(b => {
    const bookingDate = new Date(b.date || b.tour_date);
    bookingDate.setHours(0, 0, 0, 0);
    return bookingDate > tomorrow && b.status !== 'cancelled';
  }).sort((a, b) => new Date(a.date || a.tour_date) - new Date(b.date || b.tour_date));

  // Use stats from API or calculate fallback
  const paidBookings = bookings.filter(b => b.payment_status === 'paid');
  const totalRevenue = stats?.total_revenue || paidBookings.reduce((sum, b) => sum + (b.total_price || 0), 0);
  const commissionRate = stats?.commission_rate || operatorData.commission_rate || 15;
  const operatorProfit = stats?.operator_profit || (totalRevenue * (1 - commissionRate / 100));

  // Get operator's tours
  const operatorTours = tours;

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div className="bg-primary text-white">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold">
                  {operatorData.company_name || operatorData.name}
                </h1>
                <p className="text-white/80 text-sm">Provider Dashboard</p>
              </div>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/20">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{formatPrice(operatorProfit)}</p>
                <p className="text-sm text-muted-foreground">Your Earnings</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatPrice(totalRevenue)}</p>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{paidBookings.length}</p>
                <p className="text-sm text-muted-foreground">Total Bookings</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Users className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{operatorTours.length}</p>
                <p className="text-sm text-muted-foreground">Active Tours</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Today's & Tomorrow's Tours */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Today */}
          <Card className="rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                Today's Tours
              </h2>
              <Badge variant="destructive">{todayBookings.length}</Badge>
            </div>
            
            {todayBookings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No tours scheduled for today</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {todayBookings.map((booking) => (
                  <div key={booking.id} className="p-4 bg-red-50/50 border border-red-100 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-bold">{booking.booker_name || 'Guest'}</p>
                        <p className="text-sm text-primary">{booking.tour_title}</p>
                      </div>
                      <Badge className={booking.payment_status === 'paid' ? 'bg-green-500' : 'bg-amber-500'}>
                        {booking.payment_status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{formatTime(booking.time)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{booking.guests} guests</span>
                      </div>
                      <div className="text-right font-semibold text-green-600">
                        {formatPrice(booking.total_price)}
                      </div>
                    </div>
                    {booking.booker_phone && (
                      <div className="flex items-center gap-1 text-sm mt-2 text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        <span>{booking.booker_phone}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Tomorrow */}
          <Card className="rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
                Tomorrow's Tours
              </h2>
              <Badge variant="secondary">{tomorrowBookings.length}</Badge>
            </div>
            
            {tomorrowBookings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No tours scheduled for tomorrow</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {tomorrowBookings.map((booking) => (
                  <div key={booking.id} className="p-4 bg-amber-50/50 border border-amber-100 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-bold">{booking.booker_name || 'Guest'}</p>
                        <p className="text-sm text-primary">{booking.tour_title}</p>
                      </div>
                      <Badge className={booking.payment_status === 'paid' ? 'bg-green-500' : 'bg-amber-500'}>
                        {booking.payment_status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{formatTime(booking.time)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{booking.guests} guests</span>
                      </div>
                      <div className="text-right font-semibold text-green-600">
                        {formatPrice(booking.total_price)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Future Bookings */}
        <Card className="rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold">Upcoming Bookings</h2>
            <Badge variant="outline">{futureBookings.length} upcoming</Badge>
          </div>
          
          {futureBookings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No upcoming bookings</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-semibold">Customer</th>
                    <th className="text-left p-3 font-semibold">Tour</th>
                    <th className="text-left p-3 font-semibold">Date</th>
                    <th className="text-left p-3 font-semibold">Time</th>
                    <th className="text-left p-3 font-semibold">Guests</th>
                    <th className="text-right p-3 font-semibold">Amount</th>
                    <th className="text-left p-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {futureBookings.slice(0, 10).map((booking) => (
                    <tr key={booking.id} className="border-t">
                      <td className="p-3">
                        <div className="font-medium">{booking.booker_name || 'Guest'}</div>
                        <div className="text-xs text-muted-foreground">{booking.booker_email}</div>
                      </td>
                      <td className="p-3">{booking.tour_title}</td>
                      <td className="p-3">{formatDate(booking.date || booking.tour_date)}</td>
                      <td className="p-3">{formatTime(booking.time)}</td>
                      <td className="p-3">{booking.guests}</td>
                      <td className="p-3 text-right font-semibold">{formatPrice(booking.total_price)}</td>
                      <td className="p-3">
                        <Badge variant={booking.payment_status === 'paid' ? 'default' : 'secondary'}>
                          {booking.payment_status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Your Tours */}
        <Card className="rounded-xl p-6">
          <h2 className="font-heading text-xl font-bold mb-4">Your Tours</h2>
          {operatorTours.length === 0 ? (
            <p className="text-muted-foreground">No tours assigned yet. Contact Tour Finder to add your tours.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {operatorTours.map((tour) => {
                const tourBookings = paidBookings.filter(b => b.tour_id === tour.id);
                const tourRevenue = tourBookings.reduce((sum, b) => sum + (b.total_price || 0), 0);
                return (
                  <Card key={tour.id} className="p-4 border">
                    <div className="flex items-start gap-3">
                      {tour.image_url && (
                        <img 
                          src={tour.image_url} 
                          alt={tour.title}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold">{tour.title}</h3>
                        <p className="text-sm text-muted-foreground">{formatPrice(tour.price)} / person</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span>{tourBookings.length} bookings</span>
                          <span className="text-green-600 font-medium">{formatPrice(tourRevenue)}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
